<template>
  <div class="navigation-container">
    <div
      class="mobile-navigation"
      data-cy="cmp-mobile-navigation"
      :class="{ 'mobile-navigation--hide': mobileNavigationExpanded }"
    >
      <div class="mobile-navigation-menu-box">
        <Icon
          name="navigation-menu"
          :size="24"
          class="mobile-navigation-menu-icon"
          @click="expandMobileNavigation"
        />
      </div>
      <Logo class="logo" />
      <!-- below element is used for even flex spacing -->
      <div class="mobile-navigation-menu-box" />
    </div>
    <div
      v-if="mobileNavigationExpanded"
      class="mobile-navigation-backdrop"
      @click="hideMobileNavigation"
    />
    <div
      data-cy="cmp-navigation"
      class="navigation"
      :class="{ 'navigation--mobile-expanded': mobileNavigationExpanded }"
    >
      <!-- TODO: Take care of this in the new design -->
      <div class="main">
        <GodModeBar
          v-if="me.super_user"
          :link="hubLink"
          :link-text="isCustomerApp ?
            $t('customer.back_to_hub') :
            $t('partner.navigation.back_to_hub')"
        >
          {{ isCustomerApp ? $t('customer.logged_in_as') : $t('partner.navigation.logged_in_as') }}
          <strong>{{ isCustomerApp ? me.customer.name : me.partner.name }}</strong>
        </GodModeBar>
        <router-link
          class="logo-link"
          to="/"
          data-cy="cmp-logo"
        >
          <Logo class="logo" />
        </router-link>

        <nav>
          <NavigationItem
            v-for="(link, index) in links"
            :key="index"
            :link="link"
            :navigation-is-collapsed="navigationIsCollapsed"
            @hide-mobile-navigation="hideMobileNavigation"
          />
        </nav>
      </div>

      <div class="sub">
        <nav v-if="submenuLinks">
          <router-link
            v-for="link in submenuLinks"
            :key="link.key"
            class="menu-link"
            :to="link.to"
            active-class="menu-link--active"
            data-cy="cmp-sub-navigation-item"
          >
            <Icon
              :name="link.iconName"
              class="menu-link-icon"
              :size="navigationIsCollapsed ? 18 : 16"
              style="margin-bottom: 2px"
            /><span class="menu-link-text">{{ link.label }}</span>
          </router-link>
        </nav>
        <router-link
          class="avatar-link"
          :to="avatar.to"
          active-class="avatar-link--active"
          data-cy="cmp-user-profile"
        >
          <div class="initials">
            <Avatar
              :size="navigationIsCollapsed ? 'small' : 'default'"
              :text="avatar.initials"
            />
          </div>
          <div class="info">
            <span
              class="name"
              :title="avatar.label"
            >{{ avatar.label }}</span>
            <span
              class="company"
              :title="avatar.subLabel"
            >{{
              avatar.subLabel
            }}</span>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { env } from '@officeguru/webapp-shared/src/helpers/environment';
import GodModeBar from './GodModeBar.vue';
import Logo from './Logo.vue';
import Icon from './Icon.vue';
import Avatar from './Avatar.vue';
import NavigationItem from './NavigationItem.vue';

export default {
  components: {
    GodModeBar,
    Logo,
    Icon,
    Avatar,
    NavigationItem,
  },
  props: {
    links: {
      type: Array,
      required: true,
    },
    submenuLinks: {
      type: Array,
      default: null,
    },
    avatar: {
      type: Object,
      required: true,
    },
    application: {
      type: String,
      required: true,
      validator(value) {
        return ['customer', 'partner'].includes(value);
      },
    },
  },
  data() {
    return {
      hubLink: env.getEnvironmentUrl(),
      mobileNavigationExpanded: false,
    };
  },
  computed: {
    me() {
      return this.$store.getters['users/me'];
    },
    navigationIsCollapsed() {
      return this.$store.getters['ui/isNavigationHidden'];
    },
    isCustomerApp() {
      return this.application === 'customer';
    },
    isPartnerApp() {
      return this.application === 'partner';
    },
  },
  watch: {
    $route() {
      this.hideMobileNavigation();
    },
  },
  methods: {
    expandMobileNavigation() {
      this.mobileNavigationExpanded = true;
    },
    hideMobileNavigation() {
      this.mobileNavigationExpanded = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../style/main";

.navigation {
  display: flex;
  flex-direction: column;
  width: 208px;
  background: $color-misc-brand-desaturated;
  justify-content: space-between;
  height: 100vh;
  position: fixed;
  margin-left: 0;
  transition: margin .1s ease-in-out, height .1s ease-in-out, margin-left .2s;
  z-index: 100;
  overflow-y: auto;

  .navigation-collapsed & {
    @include small-up {
      width: 50px;

      .menu-link {
        margin-left: 0;
        display: flex;
        justify-content: center;
        padding: 16px;
      }

      .badge {
        position: absolute;
        margin-top: -26px;
      }

      .menu-link-icon {
        margin-right: 0;
      }

      .menu-link-text {
        display: none;
      }

      .avatar-link {
        padding: 16px;
        justify-content: center;

        /* stylelint-disable-next-line */
        .name,
        .company {
          display: none;
        }

        /* stylelint-disable-next-line */
        .initials {
          margin-right: 0;
        }
      }

      .logo {
        width: 25px;
      }
    }
  }

  .beamerAnnouncementBarTopActive & {
    height: calc(100vh - 55px);
  }
}

.expand,
.collapse {
  position: absolute;
  right: -3px;
  top: 50%;
  background: none;
  color: $color-grey-tinted-300;
  cursor: pointer;

  @media (min-width: 1280px) {
    display: none !important;
  }
}

.expand {
  display: none;
  transform: rotate(90deg);

  .navigation-collapsed & {
    display: block;
  }
}

.collapse {
  display: block;
  transform: rotate(-90deg);

  .navigation-collapsed & {
    display: none;
  }
}

.main {
  display: flex;
  flex-direction: column;
}

.sub {
  display: flex;
  flex-direction: column;
}

.logo-link {
  display: flex;
  width: 100%;
  justify-content: center;
}

.logo {
  width: 40px;
  padding: 40px 0;
}

nav {
  display: flex;
  flex-wrap: wrap;
  list-style-type: none;
  justify-content: center;
}

.menu-link {
  @include font-body-14-normal;
  color: $color-grey-tinted-300;
  display: flex;
  align-items: center;
  flex: 1 0 100%;
  text-decoration: none;
  padding: 12px;
  will-change: color;
  transition: font .2s ease-in-out, color .2s ease-in-out;
  padding-left: 32px;

  &--active {
    @include font-body-14-semi-bold;
    color: $color-grey-tinted-0;
  }

  &:hover {
    color: $color-grey-tinted-0;
  }
}

.menu-link-icon {
  margin-right: 12px;
}

.avatar-link {
  display: flex;
  align-items: center;
  padding: 20px 26px;
  text-decoration: none;

  &:hover {

    .name,
    .company {
      color: $color-grey-tinted-0;
    }
  }

  &--active {

    .name {
      @include font-body-14-semi-bold;
      color: $color-grey-tinted-0;
    }

    .company {
      @include font-caption-medium;
      color: $color-grey-tinted-0;
    }
  }
}

.info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.name {
  @include overflow-ellipsis;

  @include font-body-14-medium;
  color: $color-grey-tinted-400;
  will-change: color;
  transition: color .2s ease-in-out;
}

.company {
  @include overflow-ellipsis;

  @include font-caption-normal;
  color: $color-grey-tinted-400;
  will-change: color;
  transition: color .2s ease-in-out;
}

.initials {
  margin-right: 12px;
}

.distanced {
  margin-top: 24px;
}

.mobile-navigation {
  display: none;
}

@include small-down {

  .navigation {
    margin-left: -208px;
  }

  .mobile-navigation {
    display: flex;
    background: $color-misc-brand-desaturated;
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 50px;
    z-index: 50;
    justify-content: space-between;
    align-items: center;
    padding: 0 24px;
    transition: all .2s;

    &-menu-box {
      width: 50px;
    }

    &-menu-icon {
      cursor: pointer;
      color: #fff;
    }

    &--hide {
      margin-top: -50px !important;
    }
  }

  .navigation--mobile-expanded {
    margin-left: 0;
  }
}

.mobile-navigation-backdrop {
  background: rgba(0, 0, 0, .5);
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 40;
  transition: all .2s;
}

</style>
